import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

/* ─────────────────── Formatos compartidos ─────────────────── */

export function formatMoney(amount: string | number, currency = 'PYG'): string {
  const n = Number(amount) || 0;
  if (currency === 'PYG') return `Gs ${Math.round(n).toLocaleString('es-PY')}`;
  return `${currency} ${n.toLocaleString('es-PY', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export function formatQty(q: string | number): string {
  const n = Number(q);
  return Number.isInteger(n) ? String(n) : n.toLocaleString('es-PY');
}

/* ─────────────────── Ítems del documento ─────────────────── */

export interface DocumentItem {
  id: string;
  description: string;
  quantity: string | number;
  unitPrice: string | number;
  subtotal: string | number;
  taxRate?: number;
}

const TAX_LABEL = (rate?: number) => {
  if (rate == null) return '';
  if (rate === 0) return 'Exenta';
  return `${rate}%`;
};

export function DocumentItemsTable({
  items,
  currency = 'PYG',
  showTax = false,
}: {
  items: DocumentItem[];
  currency?: string;
  showTax?: boolean;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <Table className="min-w-[34rem]">
        <TableHeader>
          <TableRow className="bg-muted/40 hover:bg-muted/40">
            <TableHead>Descripción</TableHead>
            <TableHead className="text-right">Cant.</TableHead>
            <TableHead className="text-right">P. unit.</TableHead>
            {showTax && <TableHead className="text-right">IVA</TableHead>}
            <TableHead className="text-right">Subtotal</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.id}>
              <TableCell className="font-medium">{it.description}</TableCell>
              <TableCell className="text-right tabular-nums">{formatQty(it.quantity)}</TableCell>
              <TableCell className="text-right tabular-nums">
                {formatMoney(it.unitPrice, currency)}
              </TableCell>
              {showTax && (
                <TableCell className="text-right text-muted-foreground tabular-nums">
                  {TAX_LABEL(it.taxRate)}
                </TableCell>
              )}
              <TableCell className="text-right tabular-nums">
                {formatMoney(it.subtotal, currency)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/* ─────────────────── Totales del documento ─────────────────── */

export interface TotalLine {
  label: string;
  value: string | number;
  /** Línea destacada (total final) con borde superior y texto fuerte. */
  emphasis?: boolean;
}

export function DocumentTotals({
  lines,
  currency = 'PYG',
}: {
  lines: TotalLine[];
  currency?: string;
}) {
  return (
    <div className="flex w-full justify-end">
      <dl className="w-full max-w-xs min-w-0 space-y-1.5 text-sm sm:max-w-sm">
        {lines.map((l, i) => (
          <div
            key={i}
            className={cn(
              'flex items-center justify-between gap-2',
              l.emphasis
                ? 'mt-1 border-t border-border pt-2 text-base font-semibold text-foreground'
                : 'text-muted-foreground'
            )}
          >
            <dt className="truncate">{l.label}</dt>
            <dd className="tabular-nums whitespace-nowrap">{formatMoney(l.value, currency)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/* ─────────────────── Bloque de metadatos (def-list en grid) ─────────────────── */

export interface MetaItem {
  label: string;
  value: ReactNode;
}

export function DocumentMetaGrid({ items }: { items: MetaItem[] }) {
  const visible = items.filter((m) => m.value != null && m.value !== '');
  if (visible.length === 0) return null;
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
      {visible.map((m, i) => (
        <div key={i} className="space-y-0.5">
          <dt className="text-xs text-muted-foreground">{m.label}</dt>
          <dd className="text-sm font-medium break-words">{m.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/* ─────────────────── Cabecera del documento (cliente + estado) ─────────────────── */

export function DocumentClientBlock({
  name,
  doc,
  badge,
}: {
  name: string;
  doc?: string | null;
  badge?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-border bg-muted/30 p-4">
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="text-xs text-muted-foreground">Cliente</div>
        <div className="truncate font-semibold">{name || '—'}</div>
        {doc && <div className="font-mono text-xs text-muted-foreground">{doc}</div>}
      </div>
      {badge && <div className="shrink-0">{badge}</div>}
    </div>
  );
}

/** Pequeño wrapper de sección con título opcional. */
export function DocumentSection({
  title,
  children,
  className,
}: {
  title?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('space-y-2', className)}>
      {title && (
        <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          {title}
        </h3>
      )}
      {children}
    </section>
  );
}
